/**
 * useProductOnboarding - fetches and manages in-product onboarding checklist state.
 *
 * Step scopes:
 *   Tenant-level (infra): connector_added, data_model_built
 *   User-level (personal): first_question_asked, widget_added, widget_summarized
 *
 * Usage:
 *   const { steps, completedCount, currentStepId, isDismissed, shouldShow, completeStep, dismiss } = useProductOnboarding();
 *
 * For components that only need to fire a completion event without the full hook:
 *   import { markProductStep } from '../hooks/useProductOnboarding';
 *   markProductStep('first_question_asked');
 */
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { buildApiUrl } from '../config';
import { trackProductOnboardingStepCompleted } from '../utils/analytics';

export type ProductStepId =
  | 'connector_added'
  | 'data_model_built'
  | 'first_question_asked'
  | 'widget_added'
  | 'widget_summarized';

export interface ProductStepPayload {
  dashboard_id?: string;
}

export interface ProductStep {
  id: ProductStepId;
  label: string;
  description: string;
  cta_label: string;
  cta_route: string;
  scope: 'tenant' | 'user';
  completed: boolean;
  completed_at: string | null;
}

export interface ProductOnboardingState {
  dismissed: boolean;
  steps: ProductStep[];
  starter_questions?: string[];
  completed_count: number;
  total_count: number;
  current_step_id: ProductStepId | null;
}

// Custom event name used to notify the hook when markProductStep is called
// from outside (e.g. from chat hooks or submitAddToDashboard).
const STEP_EVENT = 'klaris:product-step-complete';
/** Keep in sync with SyncJobProvider (avoid importing hook module into provider). */
const SYNC_JOB_FINISHED_EVENT = 'klaris:sync-job-finished';

function getTenantId(): string | null {
  try {
    const raw = localStorage.getItem('klaris_tenant');
    if (!raw) return null;
    return JSON.parse(raw).tenant_id ?? null;
  } catch {
    return null;
  }
}

function getToken(): string | null {
  try { return localStorage.getItem('klaris_jwt'); } catch { return null; }
}

/**
 * Fire-and-forget: mark a step complete from anywhere in the app.
 * Dispatches a DOM event so the mounted hook refreshes its state.
 */
export async function markProductStep(stepId: ProductStepId, data?: ProductStepPayload): Promise<void> {
  const tenantId = getTenantId();
  const token = getToken();
  if (!tenantId || !token) return;
  try {
    const resp = await fetch(
      buildApiUrl(`/api/v1/tenants/${tenantId}/product-onboarding/complete-step`),
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ step: stepId, data: data || undefined }),
      }
    );
    if (resp.ok) {
      trackProductOnboardingStepCompleted({
        step_id: stepId,
        ...(data?.dashboard_id ? { dashboard_id: data.dashboard_id } : {}),
      });
      window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: { step: stepId, data } }));
    }
  } catch {
    // Non-critical - silently ignore
  }
}

export function useProductOnboarding() {
  const [state, setState] = useState<ProductOnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const tenantId = getTenantId();
  const token = getToken();

  // Only show for self_serve tenants
  const isEligible = (() => {
    try {
      const raw = localStorage.getItem('klaris_tenant');
      if (!raw) return false;
      return JSON.parse(raw).onboarding_source === 'self_serve';
    } catch {
      return false;
    }
  })();

  const fetchState = useCallback(async () => {
    if (!tenantId || !token) {
      setLoading(false);
      return;
    }
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/product-onboarding`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (resp.ok) {
        const data: ProductOnboardingState = await resp.json();
        setState(data);
      }
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [tenantId, token]);

  // Initial load + whenever the user navigates (server may have marked steps while they were elsewhere).
  useEffect(() => {
    fetchState();
  }, [fetchState, location.pathname]);

  // Re-fetch whenever markProductStep fires a completion event
  useEffect(() => {
    const handler = () => fetchState();
    window.addEventListener(STEP_EVENT, handler);
    return () => window.removeEventListener(STEP_EVENT, handler);
  }, [fetchState]);

  // Re-fetch when a background sync job finishes (backend marks data_model_built; client was not on job page).
  useEffect(() => {
    const handler = () => fetchState();
    window.addEventListener(SYNC_JOB_FINISHED_EVENT, handler);
    return () => window.removeEventListener(SYNC_JOB_FINISHED_EVENT, handler);
  }, [fetchState]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchState();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchState]);

  const completeStep = useCallback(async (stepId: ProductStepId) => {
    await markProductStep(stepId);
    // fetchState is triggered by the dispatched event above
  }, []);

  const dismiss = useCallback(async () => {
    if (!tenantId || !token) return;
    try {
      const resp = await fetch(
        buildApiUrl(`/api/v1/tenants/${tenantId}/product-onboarding/dismiss`),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      if (resp.ok) {
        const data: ProductOnboardingState = await resp.json();
        setState(data);
      }
    } catch { /* non-critical */ }
  }, [tenantId, token]);

  const allDone = state ? state.completed_count === state.total_count : false;
  const shouldShow = isEligible && !loading && state !== null && !state.dismissed && !allDone;

  return {
    steps: state?.steps ?? [],
    starterQuestions: state?.starter_questions ?? [],
    completedCount: state?.completed_count ?? 0,
    totalCount: state?.total_count ?? 4,
    currentStepId: state?.current_step_id ?? null,
    isDismissed: state?.dismissed ?? false,
    /** True only for self-serve signup tenants (`onboarding_source === 'self_serve'` in `klaris_tenant`). */
    isSelfServeProductOnboarding: isEligible,
    shouldShow,
    loading,
    completeStep,
    dismiss,
  };
}
